import { createApp } from 'vue';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import Aura from '@primevue/themes/aura';
import { config, dom, library } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import {
  faArrowDownLeft,
  faArrowLeft,
  faArrowRight,
  faArrowUpRight,
  faArrowUpRightFromSquare,
  faBan,
  faBars,
  faBell,
  faBuilding,
  faCalendarPlus,
  faCheck,
  faChevronDown,
  faCircleCheck,
  faCircleQuestion,
  faEllipsisVertical,
  faEnvelope,
  faFilter,
  faFloppyDisk,
  faHouse,
  faIdCard,
  faInputPassword,
  faLock,
  faMagnifyingGlass,
  faMobileScreen,
  faPenToSquare,
  faPhone,
  faPlus,
  faRightFromBracket,
  faRightToBracket,
  faServer,
  faSpinner,
  faTicket,
  faTrash,
  faUser,
  faUsers,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { faUser as farUser } from '@fortawesome/pro-regular-svg-icons';
import './style.css';
import App from './App.vue';
import { router } from '@/app/router';
import { i18n, resolveLocale, setLocale } from '@/shared/i18n';
import { useAuthStore } from '@/shared/stores/auth';

// --- Font Awesome (SVG core, tree-shaken) -------------------------------
// We do NOT ship the full webfont CSS. Only icons imported and registered
// below are bundled, then dom.watch() swaps <i class="fa-* fa-*"> markup
// (including PrimeVue `icon` props) for inline SVG.
//
// To use a NEW icon anywhere in the app:
//   1. import it here from pro-solid-svg-icons (and/or pro-regular-svg-icons)
//   2. add it to the library.add(...) call below
// An unregistered icon class renders nothing — there is no global fallback.
// ------------------------------------------------------------------------

// We import styles.css explicitly above, so disable runtime CSS injection.
config.autoAddCss = false;

library.add(
  // solid (fa-solid / fas)
  faUser, faInputPassword, faSpinner, faArrowRight, faHouse,
  faRightFromBracket, faCircleQuestion, faBell, faBars, faChevronDown,
  faUsers, faFilter, faIdCard, faPhone, faBuilding,
  faMagnifyingGlass, faCircleCheck, faBan, faFloppyDisk,
  faArrowDownLeft, faArrowLeft, faArrowUpRight, faArrowUpRightFromSquare, faEnvelope, faLock,
  faMobileScreen, faCalendarPlus, faRightToBracket,
  faPlus, faPenToSquare, faTrash, faEllipsisVertical,
  faCheck, faXmark, faTicket, faServer,
  // regular (fa-regular / far)
  farUser
);

// Replace <i class="fa-* fa-*"> markup (incl. PrimeVue icon props) with inline SVG.
dom.watch();

const app = createApp(App);

app.use(createPinia());
app.use(i18n);

// Pinia is active now, so the auth store has rehydrated from sessionStorage.
// Pick the initial locale from the (possibly reloaded) user, else the browser.
setLocale(resolveLocale(useAuthStore().user?.language ?? null));

app.use(router);
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: '.app-dark',
      cssLayer: {
        name: 'primevue',
        order: 'tailwind-base, primevue, tailwind-utilities',
      },
    },
  },
});
app.use(ToastService);
app.directive('tooltip', Tooltip);

app.mount('#app');
